import { useRef, useState } from "react";
import { notification } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useDispatch, useStore } from "react-redux";
import generateMC from "../../../../utils/generateMC";
import generatePC from "../../../../utils/generatePC";
import { getInventories } from "../../../toolkit/slices/inventories/inventorySlice";
import {
  enrichBdfInventoryFromRedux,
  getBdfLineKey,
  hasOverlapWithSiblingBdfLines,
  withBdfLineKey,
} from "../../../../utils/bdfLineKey";
import {
  buildBdfImportTemplate,
  normalizeCode,
  parseBdfExcelBuffer,
  planBdfExcelImport,
} from "../../../../utils/bdfExcelImport";

dayjs.extend(customParseFormat);

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const INITIAL_STATE = {
  open: false,
  status: "loading",
  fileName: "",
  error: null,
  foundHeaders: null,
  info: null,
  plan: null,
};

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

// Same shape the "Add Inventories" picker builds for every inventory.
const mapReduxInvForImport = (ele) => ({
  _id: ele._id,
  codeNo: ele.CodeNo,
  location: ele?.locations?.name,
  width: ele.width,
  height: ele.height,
  Bookings: ele.Bookings,
  blockedDates: ele?.blockedDates,
  premiumBlock: ele?.premiumBlock,
  rpm: ele.ratePerMonth,
  Illu: ele.Illu,
  unit: ele?.unit || 1,
  isOverlapPossible: ele?.isOverlapPossible,
  isArchived: ele?.isArchived,
  removedOn: ele?.removedOn,
  typeOfMedia: ele?.typeOfMedia,
  mountingCharge: generateMC(
    ele.width,
    ele.height,
    ele.Illu,
    ele?.unit,
    ele?.typeOfMedia?.name,
  ),
  printingCharge: generatePC(
    ele.width,
    ele.height,
    ele.Illu,
    ele?.unit,
    ele?.typeOfMedia?.name,
  ),
  specialRatePerMonth: ele.ratePerMonth,
  ratePerMonth: ele.ratePerMonth,
  checked: false,
});

const fetchInventoryList = async (endpoint, key) => {
  const response = await fetch(`${apiBaseUrl}/inventoryManagement/${endpoint}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      authorization: `Token ${window.localStorage.getItem("token")}`,
    },
  });
  const data = await response.json();
  return data?.success ? data[key] || [] : [];
};

const useBdfExcelImport = ({
  inventories,
  setInventories,
  allowDuplicateInventories,
  packageRate,
  handleChange,
  isDateBlocked,
  calcSpecialRate,
  canAddToCart,
  mapRemovedInvForPicker,
  mapTradingInvForPicker,
  // Optional overrides so other screens (e.g. premium block) can reuse the import.
  columnKeys, // limit the file columns that are read
  chargesEnabled = true,
  mapInventory = mapReduxInvForImport,
  createLine,
  includeRemovedAndTrading = true,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const excelInputRef = useRef(null);
  const requestRef = useRef(0);
  const [excelImport, setExcelImport] = useState(INITIAL_STATE);

  const buildLookup = async (codes) => {
    await dispatch(getInventories());
    const fresh = store.getState().inventoriesList.inventories || [];
    if (fresh.length === 0) {
      throw new Error("Could not load inventories. Please try again.");
    }

    const lookup = new Map();
    const add = (code, inventory) => {
      const key = normalizeCode(code);
      if (!key) return;
      const list = lookup.get(key) || [];
      if (!list.some((x) => x._id === inventory._id)) list.push(inventory);
      lookup.set(key, list);
    };
    fresh.forEach((ele) => add(ele.CodeNo, mapInventory(ele)));

    // Removed and trading inventories are only looked up for codes not found above.
    const unresolved = new Set(
      codes.map(normalizeCode).filter((key) => key && !lookup.has(key)),
    );
    if (includeRemovedAndTrading && unresolved.size > 0) {
      try {
        const [removed, trading] = await Promise.all([
          fetchInventoryList("getAllRemovedInventories", "allRemovedInventories"),
          fetchInventoryList("getAllTradingInventories", "allTradingInventories"),
        ]);
        removed.forEach((ele) => {
          if (unresolved.has(normalizeCode(ele.CodeNo))) {
            add(ele.CodeNo, mapRemovedInvForPicker(ele));
          }
        });
        trading.forEach((ele) => {
          if (unresolved.has(normalizeCode(ele.CodeNo))) {
            add(ele.CodeNo, mapTradingInvForPicker(ele));
          }
        });
      } catch (error) {
        console.error("Could not load removed/trading inventories:", error);
      }
    }
    return { fresh, lookup };
  };

  const update = (token, patch) => {
    if (token !== requestRef.current) return;
    setExcelImport((prev) => ({ ...prev, ...patch }));
  };

  const handleExcelFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      notification.error({
        message: "Please choose an Excel (.xlsx, .xls) or .csv file.",
      });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      notification.error({ message: "The file is larger than 5 MB." });
      return;
    }

    requestRef.current += 1;
    const token = requestRef.current;
    setExcelImport({
      ...INITIAL_STATE,
      open: true,
      status: "loading",
      fileName: file.name,
    });

    try {
      const buffer = await readFileAsArrayBuffer(file);
      const parsed = parseBdfExcelBuffer(buffer, { columnKeys });
      if (!parsed.ok) {
        update(token, {
          status: "error",
          error: parsed.error,
          foundHeaders: parsed.foundHeaders || null,
        });
        return;
      }

      const { fresh, lookup } = await buildLookup(
        parsed.rows.map((row) => row.code).filter(Boolean),
      );
      const plan = planBdfExcelImport({
        rows: parsed.rows,
        existingInventories: inventories || [],
        allowDuplicateInventories,
        packageRate,
        resolveInventory: (code) => {
          const list = lookup.get(normalizeCode(code));
          if (!list || list.length === 0) return null;
          if (list.length > 1) return { ambiguous: true };
          return { inventory: list[0] };
        },
        chargesEnabled,
        createLine: createLine
          ? (item) => createLine({ ...item }, fresh)
          : (item) =>
              withBdfLineKey(enrichBdfInventoryFromRedux({ ...item }, fresh)),
        isDateBlocked: (day, record, working) =>
          isDateBlocked(
            dayjs(day, "DD-MM-YYYY", true),
            record.Bookings,
            record.blockedDates,
            record.premiumBlock,
            record,
            {
              blockSiblingPeriods: allowDuplicateInventories,
              inventories: working,
            },
          ),
        hasSiblingOverlap: (working, candidate, start, end) =>
          hasOverlapWithSiblingBdfLines(
            working,
            getBdfLineKey(candidate),
            candidate._id,
            start,
            end,
          ),
        calcSpecialRate,
        canAddToCart,
      });

      update(token, {
        status: "preview",
        info: {
          sheetName: parsed.sheetName,
          matched: parsed.matched,
          ignored: parsed.ignored,
          warnings: parsed.warnings,
        },
        plan,
      });
    } catch (error) {
      console.error("Excel import failed:", error);
      update(token, {
        status: "error",
        error: error?.message || "Could not process the file.",
      });
    }
  };

  const closeExcelImport = () => {
    requestRef.current += 1;
    setExcelImport((prev) => ({ ...prev, open: false }));
  };

  const confirmExcelImport = () => {
    const plan = excelImport.plan;
    if (!plan || plan.readyCount === 0) return;
    const newLines = plan.lines;
    const finalList = [...(inventories || []), ...newLines];

    setInventories((prev) => [...prev, ...newLines]);

    if (finalList.every((item) => item.endDate)) {
      let maxDifferenceInDays = 0;
      finalList.forEach((item) => {
        if (item.startDate && item.endDate) {
          const start = dayjs(item.startDate, "DD-MM-YYYY");
          const end = dayjs(item.endDate, "DD-MM-YYYY");
          if (start.isValid() && end.isValid()) {
            maxDifferenceInDays = Math.max(
              maxDifferenceInDays,
              end.diff(start, "day"),
            );
          }
        }
      });
      if (handleChange) handleChange("campaignDuration", maxDifferenceInDays + 1);
    }

    notification.success({
      message: `${newLines.length} inventor${
        newLines.length === 1 ? "y" : "ies"
      } imported from Excel`,
      description:
        plan.errorCount > 0
          ? `${plan.errorCount} row${plan.errorCount === 1 ? " was" : "s were"} skipped.`
          : undefined,
    });
    closeExcelImport();
  };

  const downloadExcelTemplate = () => {
    const bytes = buildBdfImportTemplate({ columnKeys });
    const blob = new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "BDF_Inventory_Import_Template.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const openExcelPicker = () => excelInputRef.current?.click();

  // Opens the dialog on the format guide; the file is chosen from inside it.
  const openExcelImport = () => {
    requestRef.current += 1;
    setExcelImport({ ...INITIAL_STATE, open: true, status: "intro" });
  };

  return {
    excelInputRef,
    excelImport,
    handleExcelFileChange,
    closeExcelImport,
    confirmExcelImport,
    downloadExcelTemplate,
    openExcelPicker,
    openExcelImport,
  };
};

export default useBdfExcelImport;
